import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Form, Grid, message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type ConsentsAndDeclarationsWithPermissionsSpecsFragment } from '../../../api/fragments/ConsentsAndDeclarationsWithPermissionsSpecs';
import {
    DeleteConsentsAndDeclarationsDocument,
    type DeleteConsentsAndDeclarationsMutation,
    type DeleteConsentsAndDeclarationsMutationVariables,
} from '../../../api/mutations/deleteConsentsAndDeclarations';
import {
    UpdateCheckboxConsentsAndDeclarationsDocument,
    type UpdateCheckboxConsentsAndDeclarationsMutation,
    type UpdateCheckboxConsentsAndDeclarationsMutationVariables,
} from '../../../api/mutations/updateCheckboxConsentsAndDeclarations';
import {
    UpdateMarketingConsentsAndDeclarationsDocument,
    type UpdateMarketingConsentsAndDeclarationsMutation,
    type UpdateMarketingConsentsAndDeclarationsMutationVariables,
} from '../../../api/mutations/updateMarketingConsentsAndDeclarations';
import {
    UpdateTextConsentsAndDeclarationsDocument,
    type UpdateTextConsentsAndDeclarationsMutation,
    type UpdateTextConsentsAndDeclarationsMutationVariables,
} from '../../../api/mutations/updateTextConsentsAndDeclarations';
import { useGetModuleSpecsQuery } from '../../../api/queries/getModuleSpecs';
import {
    type CheckboxConsentsAndDeclarationsSettings,
    type ConditionSettings,
    ConsentsAndDeclarationsType,
    type MarketingConsentsAndDeclarationsSettings,
    type TextConsentsAndDeclarationsSettings,
} from '../../../api/types';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import ConsentsAndDeclarationsDetails from './ConsentsAndDeclarationsDetails';
import getConditionSettings from './getConditionSettings';
import prepareSubmissionConditionSettings from './prepareSubmissionConditionSettings';
import useConsentsAndDeclarationsValidator from './useConsentsAndDeclarationsValidator';

export type ConsentsAndDeclarationsInnerPageProps = {
    consentAndDeclaration: ConsentsAndDeclarationsWithPermissionsSpecsFragment;
};

export type cndSettings =
    | TextConsentsAndDeclarationsSettings
    | CheckboxConsentsAndDeclarationsSettings
    | MarketingConsentsAndDeclarationsSettings;

type FormValues = cndSettings & {
    conditionSettings: ConditionSettings[];
    moduleId: string;
};

const ConsentsAndDeclarationsInnerPage = ({ consentAndDeclaration }: ConsentsAndDeclarationsInnerPageProps) => {
    const { t } = useTranslation(['consentsAndDeclarations']);
    const navigate = useNavigate();
    const validate = useConsentsAndDeclarationsValidator(true);
    const { type, moduleId } = consentAndDeclaration;
    const conditionSettings = useMemo(
        () => (consentAndDeclaration.conditions ? getConditionSettings(consentAndDeclaration.conditions) : []),
        [consentAndDeclaration.conditions]
    );
    const { state } = useLocation();

    const consentsAndDeclarationsId = consentAndDeclaration.id;
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: consentAndDeclaration.versioning,
        moduleId: consentAndDeclaration.moduleId,
    });

    const apolloClient = useApolloClient();
    const screens = Grid.useBreakpoint();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { moduleId, conditionSettings: conditionSettingsInput, ...settings } = values;

            const conditionSettings = prepareSubmissionConditionSettings(conditionSettingsInput);

            // submitting message
            message.loading({
                content: t('consentsAndDeclarations:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            switch (type) {
                case ConsentsAndDeclarationsType.Text: {
                    const textCndSettings = settings as TextConsentsAndDeclarationsSettings;

                    await apolloClient
                        .mutate<
                            UpdateTextConsentsAndDeclarationsMutation,
                            UpdateTextConsentsAndDeclarationsMutationVariables
                        >({
                            mutation: UpdateTextConsentsAndDeclarationsDocument,
                            variables: {
                                id: consentsAndDeclarationsId,
                                settings: textCndSettings,
                                conditionSettings,
                                eventId: state?.eventId,
                            },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    break;
                }

                case ConsentsAndDeclarationsType.Checkbox: {
                    const checkboxCndSettings = settings as CheckboxConsentsAndDeclarationsSettings;

                    await apolloClient
                        .mutate<
                            UpdateCheckboxConsentsAndDeclarationsMutation,
                            UpdateCheckboxConsentsAndDeclarationsMutationVariables
                        >({
                            mutation: UpdateCheckboxConsentsAndDeclarationsDocument,
                            variables: {
                                id: consentsAndDeclarationsId,
                                settings: checkboxCndSettings,
                                conditionSettings,
                                eventId: state?.eventId,
                            },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    break;
                }

                case ConsentsAndDeclarationsType.Marketing: {
                    const marketingCndSettings = settings as MarketingConsentsAndDeclarationsSettings;

                    await apolloClient
                        .mutate<
                            UpdateMarketingConsentsAndDeclarationsMutation,
                            UpdateMarketingConsentsAndDeclarationsMutationVariables
                        >({
                            mutation: UpdateMarketingConsentsAndDeclarationsDocument,
                            variables: {
                                id: consentsAndDeclarationsId,
                                settings: marketingCndSettings,
                                conditionSettings,
                                eventId: state?.eventId,
                            },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    break;
                }

                default:
                    throw new Error('Consents and declarations type is not supported');
            }

            // inform about success
            message.success({
                content: t('consentsAndDeclarations:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate(-1);
        },
        [t, type, navigate, apolloClient, consentsAndDeclarationsId, state?.eventId]
    );

    const deleteConsentsAndDeclarations = useCallback(() => {
        Modal.confirm({
            title: t('consentsAndDeclarations:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('consentsAndDeclarations:deleteModal.content'),
            okText: t('consentsAndDeclarations:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('consentsAndDeclarations:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('consentsAndDeclarations:message.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with API
                    await apolloClient
                        .mutate<DeleteConsentsAndDeclarationsMutation, DeleteConsentsAndDeclarationsMutationVariables>({
                            mutation: DeleteConsentsAndDeclarationsDocument,
                            variables: { id: consentsAndDeclarationsId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('consentsAndDeclarations:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/consents');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, t, consentsAndDeclarationsId, navigate]);

    const initialValues = useMemo((): FormValues => {
        switch (consentAndDeclaration.__typename) {
            case 'TextConsentsAndDeclarations': {
                const cndSettings = {
                    ...pick(
                        [
                            'displayName',
                            'title',
                            'orderNumber',
                            'isActive',
                            'description',
                            'moduleId',
                            'purpose',
                            'dataField',
                        ],
                        consentAndDeclaration
                    ),
                };

                return {
                    ...cndSettings,
                    conditionSettings,
                };
            }

            case 'CheckboxConsentsAndDeclarations': {
                const cndSettings = {
                    ...pick(
                        [
                            'displayName',
                            'title',
                            'orderNumber',
                            'isMandatory',
                            'isActive',
                            'description',
                            'moduleId',
                            'purpose',
                            'legalMarkup',
                            'dataField',
                            'legalTextPosition',
                        ],
                        consentAndDeclaration
                    ),
                };

                return {
                    ...cndSettings,
                    conditionSettings,
                };
            }

            case 'MarketingConsentsAndDeclarations': {
                const cndSettings = {
                    ...pick(
                        [
                            'displayName',
                            'title',
                            'orderNumber',
                            'isMandatory',
                            'isActive',
                            'description',
                            'moduleId',
                            'purpose',
                            'platform',
                            'dataField',
                            'defaultChecked',
                        ],
                        consentAndDeclaration
                    ),
                };

                return {
                    ...cndSettings,
                    conditionSettings,
                };
            }

            default:
                throw new Error('Consents and declarations type is not supported');
        }
    }, [conditionSettings, consentAndDeclaration]);

    const { data } = useGetModuleSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { moduleId },
    });

    if (!data) {
        return null;
    }

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <ConsolePageWithHeader
                    extra={
                        hasPermissions(consentAndDeclaration.permissions, [permissionKind.deleteAgreement]) && (
                            <Button onClick={deleteConsentsAndDeclarations} danger>
                                {t('consentsAndDeclarations:actions.delete')}
                            </Button>
                        )
                    }
                    footer={[
                        hasPermissions(consentAndDeclaration.permissions, [permissionKind.updateAgreement]) && (
                            <Button
                                key="consentsAndDeclarationsUpdate"
                                form="consentsAndDeclarationsUpdate"
                                htmlType="submit"
                                type="primary"
                            >
                                {t('consentsAndDeclarations:actions.update')}
                            </Button>
                        ),
                    ].filter(Boolean)}
                    onBack={() => navigate('/admin/consents')}
                    title={
                        screens.md
                            ? t('consentsAndDeclarations:detailTitle', { name: initialValues.displayName })
                            : initialValues.displayName
                    }
                >
                    <Form
                        id="consentsAndDeclarationsUpdate"
                        layout="vertical"
                        name="consentsAndDeclarationsUpdate"
                        onSubmitCapture={handleSubmit}
                    >
                        <ConsentsAndDeclarationsDetails
                            disabled={
                                !hasPermissions(consentAndDeclaration.permissions, [permissionKind.updateAgreement])
                            }
                            initialValues={initialValues}
                            moduleName={data.module.displayName}
                            offset={offset}
                            type={type}
                            updated={updated}
                        />
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default ConsentsAndDeclarationsInnerPage;
